import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ConfirmActionModal } from "~/common/components/ConfirmActionModal";
import { CardInputsContainer } from "~/common/components/layouts/CardInputsContainer";
import Modal from "~/common/components/Modal";
import { Button } from "~/common/components/ui/button";
import { INTEGRATION_STATUS, INTEGRATION_TYPE } from "~/common/constants";
import { Integration } from "~/common/types/integration";
import { env } from "~/providers/env";
import { useDeleteIntegration } from "~/providers/hooks";
import { useCurrentTeamMemberContext } from "~/providers/hooks/useCurrentTeamMemberContext";
import { IntegrationForm } from "./IntegrationForm";

const INTEGRATION_CONFIG_MAP = {
  [INTEGRATION_TYPE.STRIPE]: {
    title: "Stripe",
    link: "https://psychnow.com/userguide/payment-settings",
    description:
      "to process payments for consultation fees. All consultation request links that are configured to collect a fee will use this Stripe account.",
    confirmation:
      "Are you sure you want to disconnect your Stripe account? Consultation request links with fees will be reset to $0.",
  },
  [INTEGRATION_TYPE.ZOOM]: {
    title: "Zoom",
    link: "https://developers.zoom.us/docs/internal-apps/s2s-oauth/#get-app-credentials",
    description:
      "to pull meeting transcripts, summaries, and recordings using Server-to-Server credentials. You can enter Zoom meeting IDs in the 'Progress note' step of the consultation process.",
    confirmation: "Are you sure you want to disconnect your Zoom account?",
  },
  [INTEGRATION_TYPE.ZOOM_APP]: {
    title: "Zoom App",
    link: "https://developers.zoom.us/docs/integrations/oauth/",
    description:
      "to pull meeting transcripts, summaries, and recordings via the Zoom Marketplace OAuth app. You can enter Zoom meeting IDs in the 'Progress note' step of the consultation process.",
    confirmation: "Are you sure you want to disconnect your Zoom App account?",
  },
  [INTEGRATION_TYPE.SPRUCE]: {
    title: "Spruce",
    link: "https://developer.sprucehealth.com/docs/overview",
    description:
      "to connect Spruce Health for your organization. Use an API token from your Spruce account to enable the integration.",
    confirmation: "Are you sure you want to disconnect your Spruce account?",
  },
} as const;

export const IntegrationCard = ({
  integration,
}: {
  integration: Integration;
}) => {
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const {
    organization_team_members: [organization_team_member],
  } = useCurrentTeamMemberContext();

  const { mutate, isPending } = useDeleteIntegration();

  const config = INTEGRATION_CONFIG_MAP[integration.type];
  const isZoomS2S = integration.type === INTEGRATION_TYPE.ZOOM;
  const isZoomApp = integration.type === INTEGRATION_TYPE.ZOOM_APP;
  const usesCredentialForm = !isZoomApp;

  const onCancel = () => {
    setDeleting(false);
    setEditing(false);
  };

  const onConnect = () => {
    void queryClient.invalidateQueries({
      queryKey: ["integrations", organization_team_member.organization_id],
    });
    setEditing(false);
  };

  const startZoomAppOAuth = () => {
    window.location.href = `${env.VITE_API_URL}/v1/organizations/${organization_team_member.organization_id}/integrations/zoom-app/authorize`;
  };

  const onDisconnect = () => {
    mutate(
      {
        organizationId: organization_team_member.organization_id,
        type: integration.type,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [
              "integrations",
              organization_team_member.organization_id,
            ],
          });

          setDeleting(false);
        },
      },
    );
  };

  const onPrimaryConnect = () => {
    if (isZoomApp) {
      startZoomAppOAuth();
      return;
    }
    setEditing(true);
  };

  const onSecondaryAction = () => {
    if (isZoomApp) {
      startZoomAppOAuth();
      return;
    }
    setEditing(true);
  };

  return (
    <>
      <CardInputsContainer
        title={config.title}
        actions={
          integration.status === INTEGRATION_STATUS.CONNECTED && (
            <div className="rounded-full bg-success-200 px-3 text-[13px] font-semibold text-green-800">
              Connected
            </div>
          )
        }
      >
        <div className="flex gap-10 py-2">
          <div className="flex-1">
            <p>
              {integration.status === INTEGRATION_STATUS.CONNECTED ? (
                <>{config.title} has been connected</>
              ) : (
                <a
                  className="text-primary underline"
                  href={config.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  Connect your {config.title} account
                </a>
              )}
              {config.description && <span> {config.description}</span>}
            </p>
          </div>
          <div>
            {integration.status === INTEGRATION_STATUS.CONNECTED &&
              (isZoomS2S || isZoomApp) &&
              integration.account_id && (
                <div className="mb-2 text-sm">
                  Account ID: <strong>{integration.account_id}</strong>
                </div>
              )}
            <div className="flex flex-col gap-3 md:flex-row">
              {integration.status === INTEGRATION_STATUS.DISCONNECTED ? (
                <Button
                  className="w-32 flex-1"
                  onClick={onPrimaryConnect}
                  disabled={false}
                >
                  Connect
                </Button>
              ) : (
                <>
                  <Button
                    className="w-32 flex-1"
                    variant="outline"
                    onClick={() => setDeleting(true)}
                    disabled={false}
                    isLoading={isPending}
                  >
                    Disconnect
                  </Button>
                  <Button
                    className="w-32 flex-1"
                    variant="outline"
                    onClick={onSecondaryAction}
                    isLoading={isPending}
                  >
                    {isZoomApp ? "Reconnect" : "Update"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </CardInputsContainer>
      {usesCredentialForm && (
        <Modal
          show={isEditing}
          onClose={() => setEditing(false)}
          title="Update connection"
          className="max-h-[90vh] max-w-xl overflow-auto text-sm text-gray-700 sm:w-4/5 md:w-2/3 xl:w-2/3"
        >
          <a
            className="text-primary underline"
            href={config.link}
            target="_blank"
            rel="noreferrer"
          >
            Instructions for obtaining your {config.title} API keys
          </a>
          <IntegrationForm
            integration={integration}
            onSuccess={onConnect}
            onCancel={onCancel}
          />
        </Modal>
      )}

      <ConfirmActionModal
        title="Confirm disconnect"
        description={config.confirmation}
        show={isDeleting}
        confirmButtonText="Disconnect"
        onClose={onCancel}
        cancelButtonClassName="hidden"
        onConfirm={onDisconnect}
      />
    </>
  );
};
